// ============================================================
// BodyPlate - a person's body plate at rest (plan body-map P2): the traced figure for its traits, each hit location
// the same line art under its own clip (so a mark recolours the lines themselves), a callout per location in the
// free spot beside it (cover in blue on its numeral), the attacker's sights, a readout for the location under the pointer, the
// wounds under the figure, and the viewer's lens. Plain (the actor's status with the detailed map off), the figure
// alone. Its conditions change the lines themselves, on layers of their own (fx/conditions, plan S1), plain or not.
//
// It keeps the old map's names so every spec still finds it: the svg's `<bodyType> body hit location map` label, a
// `<path style="cursor: context-menu">` per location for the armour edit, `map-zone-<loc>` sights, the numerals as
// `<text>`. Vehicles, the mass and crowds have their own plates (PlateVehicle, PlateGoo, PlateCrowd).
// ============================================================

import usePlateLane from './usePlateLane';
import useStartPulse from './useStartPulse';
import PlateLane from './fx/killTeam/PlateLane';
import { memo, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import clsx from '../../utils/clsx';
import { getBodyConfig } from '../../engine/bodyConfig';
import { plateBand } from '../../engine/plate/plateBand';
import { PLATE_TYPE_VARS } from '../../engine/plate/plateType';
import { figureBounds, type PlateArt, type PlateFigureId } from '../../engine/plate/figureArt';
import { artFrame } from '../../engine/plate/frame';
import { wrongNumber } from '../../engine/plate/daemonic';
import { seeded } from '../../engine/plate/hash';
import tx from '../../styles/PlateTraits.module.css';
import { traitHold, traitLooks, type TraitLooks } from '../../engine/plate/traitLooks';
import { withoutRefused } from '../../engine/plate/marks';
import type { Character } from '../../engine/character/types';
import { damageOf, type PlateCallout } from '../../engine/plate/plateModel';
import { CALLOUT_DY, wordsFit, type CalloutLayout, type Frame } from '../../engine/plate/calloutLayout';
import usePlateLayout from './usePlateLayout';
import { Lens, useLens } from './PlateLens';
import PlateWords, { useLastImpact } from './PlateWords';
import useOnsets from '../../hooks/useOnsets';
import { condType } from '../../engine/conditions';
import { lastHitLines, lockedLines } from '../../engine/plate/readout';
import { needsClock, type PlateState } from '../../engine/plate/plateState';
import { idleMotion, idleSeed, idleStill, scaleIdle } from '../../engine/plate/idle';
import { approach } from '../../engine/display/timeline';
import type { Target } from '../../types';
import type { Impact } from '../../engine/impacts';
import { throwAt } from '../../engine/plate/hits';
import { thrower, type Burst } from '../../engine/plate/burst';
import { fieldHold } from '../../engine/plate/fieldRing';
import usePlateState from './usePlateState';
import styles from '../../styles/BodyPlate.module.css';
import fxStyles from '../../styles/PlateFx.module.css';
import { usePlateArt } from './usePlateArt';
import usePlateClock from './usePlateClock';
import usePlateHits, { type HitPlay } from './usePlateHits';
import useHoldsStill from '../../hooks/useHoldsStill';
import { applyScene, useSceneWriter, writeScene } from './fx/scene';
import { HitDefs, hitNodes, Struck } from './fx/hits';
import { StrikeMoments } from './fx/moments';
import { moveOf } from './fx/specials/SpecialMoments';
import { guardOf, type Guard } from '../../engine/plate/moments';
import { BurstMarks } from './fx/burst';
import {
  looksMove,
  figureLook,
  needsSilhouette,
  TraitOn,
  TraitBack,
  TraitDefs,
  TraitUnder,
  traitDraw,
  traitFrame,
  type TraitFrame,
  TraitFront,
  TraitOver,
  type TraitDraw,
} from './fx/traits/TraitLayers';
import { InkSeals } from './fx/traits/Natures';
import { wrongPivot } from '../../engine/plate/natures';
import { ConditionDefs, ConditionLayers, ConditionOverlays, figureMarks, locationCondition } from './fx/conditions';
import {
  ArtDefs,
  boxBracketPath,
  CalloutLeader,
  scaleAbout,
  CoverPlus,
  Glow,
  hitOrder,
  StatusLine,
  statusWords,
  LocationClips,
  marksClass,
  Snow,
  SnowDefs,
  GlowHole,
  holesOf,
  type Regions,
  PlateHit,
  PlateLines,
  PlateSights,
  plateSize,
  lockedOn,
  dimmedBy,
  regionsOf,
  type Targeting,
  ScanUnavailable,
  usePlateUid,
  viewBoxOf,
} from './plateParts';

/** The lens (viewer setting `bodyLens`): its radius in frame units, and how much it magnifies. */

interface Props {
  figure: PlateFigureId;
  target: Target;
  side?: 'target' | 'actor';
  fill?: boolean;
  targeting?: Targeting | null;
  onLocationContextMenu?: (id: string) => void;
  /**
   * The figure alone - no callouts, marks, sights or wounds (the actor's status when the GM turns the detailed map
   * off: a player's own numbers stay off the screen).
   */
  plain?: boolean;
  /** The svg's name; a hit-location map's by default. */
  ariaLabel?: string;
  /** What just happened to this body (engine/impacts): a new one plays on the plate once (plan body-map H1). */
  impact?: Impact | null;
  /** The combatant's own sheet: its Psy Rating when the Target carries none, its codex tier (plan body-map T1). */
  sheet?: Character | null;
}

export default function BodyPlate({ figure, target, ariaLabel, ...rest }: Props) {
  const { art, failed } = usePlateArt(figure);
  const label = ariaLabel ?? `${target.bodyType || 'humanoid'} body hit location map`;
  if (failed) return <ScanUnavailable ariaLabel={label} className={plateSize(rest.fill)} />;
  return <PlateSvg art={art} target={target} figure={figure} ariaLabel={label} {...rest} />;
}

type SvgProps = Props & { art: PlateArt | null; ariaLabel: string };

function PlateSvg(props: SvgProps) {
  const { art, target, figure, side = 'target', fill = false, ariaLabel, impact, sheet = null, ...content } = props;
  const u = usePlateUid('plate');
  const looks = useMemo(() => traitLooks(target, sheet, figure), [target, sheet, figure]);
  // A Blind body refuses Blinded: it never saw (plan body-map T2).
  const conditions = useMemo(
    () => withoutRefused(target.conditions ?? [], looks.blind),
    [target.conditions, looks.blind],
  );
  const { state, dataStates, glow } = usePlateState(conditions);
  const ref = useRef<SVGSVGElement>(null);
  const { hits, drawn } = useLooksPlay(impact, target, looks, art, state.dead);
  // The idle gives way to a hit as it plays: eased out, never a jump. At Critical it sways on: the deep red says
  // it (ruled 2026-10-14).
  const idle = useIdle(art, state, u, hits.play !== null);
  const frame = useHitFrame(hits, idle, drawn && traitFrame(drawn, u, hits.play, hits.elapsed), looks.sturdy);
  const busy = needsClock(state) || hits.clocked || (drawn !== null && looksMove(drawn));
  // Only the idle sway moving: the clock may draw it at a slower pace (usePlateClock, the perf overhaul).
  usePlateClock(ref, art !== null && (busy || idle.moving), frame, { idleOnly: !busy });
  // The frame, each callout's free spot and the words' scale for this cell (plan plate-unboxed).
  const { last, status, words } = usePlateWords(impact, state, looks);
  const { callouts, layout, cell } = usePlateLayout(ref, art, target, { fill, status, words });
  const moment = useArmMoment(conditions);
  // A kill-team brother's lane (Squad Mode): the frame widens at the right by it, the figure keeps its place.
  const lane = usePlateLane(target, side, layout.frame, cell);
  const starting = useStartPulse(target.killTeam);
  const vb = lane?.lane.frame ?? layout.frame;
  const lens = useLens(vb);
  const style = {
    ...glow,
    ...PLATE_TYPE_VARS,
    '--lw': art?.lineWeight ?? 1,
    '--words-k': wordsFit(vb),
  } as CSSProperties;
  return (
    <svg
      ref={ref}
      viewBox={viewBoxOf(vb)}
      {...rootData(moment, hits.play)}
      className={clsx(plateSize(fill), styles.plate, side === 'actor' && styles.actor, state.dead && styles.dead)}
      data-band={plateBand(target, state.dead)}
      style={style}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
      data-figure={figure}
      data-states={dataStates}
      {...lens.handlers}
    >
      <MemoContent
        u={u}
        art={art}
        target={target}
        callouts={callouts}
        layout={layout}
        last={last}
        state={state}
        play={hits.play}
        shownAt={hits.shownAt}
        looks={looks}
        drawn={drawn}
        {...content}
      />
      {art && lens.at && <Lens u={u} at={lens.at} />}
      {art && lane && cell && (
        <PlateLane
          u={u}
          of={lane}
          target={target}
          conditions={conditions}
          dead={state.dead}
          side={side}
          starting={starting}
        />
      )}
    </svg>
  );
}

type ContentProps = Pick<Props, 'targeting' | 'onLocationContextMenu' | 'plain'> & {
  u: string;
  art: PlateArt | null;
  target: Target;
  /** The plate's callouts and where they stand (usePlateLayout). */
  callouts: PlateCallout[];
  layout: CalloutLayout;
  /** The last hit this plate saw, for its words (P4). */
  last: Impact | null;
  state: PlateState;
  play: HitPlay | null;
  shownAt: number;
  looks: TraitLooks;
  /** The looks as drawn this moment; null until the art is in. */
  drawn: TraitDraw | null;
};

/** What the plate holds: the figure alone (plain), or the figure with its callouts and sights, and the wounds. */
function PlateContent(props: ContentProps) {
  const { u, art, target, callouts, layout, last, state, play, shownAt, looks, drawn, plain, ...touch } = props;
  const [hot, setHot] = useState<string | null>(null);
  const figure = drawn && { u, state, play, shownAt, drawn, guard: guardOf(target), frame: layout.frame };
  if (plain) return art && figure && <PersonFigure art={art} {...figure} />;
  // The sights and the place they hold, worked out once: the figure dims round it, the words name it.
  const sights = sightsFor(target, touch.targeting);
  const locked = lockedOn(callouts, sights);
  const nameOf = (id: string) => callouts.find((c) => c.id === id)?.name ?? id;
  return (
    <>
      {art && figure && (
        <PlateBody
          art={art}
          callouts={callouts}
          layout={layout}
          figure={figure}
          hot={hot}
          onHot={setHot}
          sights={sights}
          locked={locked}
          onLocationContextMenu={touch.onLocationContextMenu}
        />
      )}
      <Status state={state} banished={looks.daemon > 0} left={layout.frame.x + 40} />
      <PlateWords
        last={last}
        looks={looks}
        nameOf={nameOf}
        frame={layout.frame}
        sights={sights && locked ? nameOf(locked) : null}
        quiet={hot !== null}
      />
    </>
  );
}

/** The sights, while a called shot is being chosen on a body that takes one; null otherwise. */
const sightsFor = (target: Target, targeting: Targeting | null | undefined) =>
  targeting && getBodyConfig(target.bodyType || 'humanoid').supportsCalledShot ? targeting : null;

/**
 * Memoised, so the lens - whose position is the plate's state and changes on every pointer move - redraws only
 * itself, never the figure, its callouts and its sights.
 */
const MemoContent = memo(PlateContent);

interface FigureProps {
  u: string;
  art: PlateArt;
  state: PlateState;
  play: HitPlay | null;
  shownAt: number;
  /** What a hit may meet before it lands: a force field, Daemonic protection. */
  guard: Guard;
  /** The body's nature drawn round it (plan body-map T1), as the plate drew it this moment. */
  drawn: TraitDraw;
  /** A location's lines' look by its marks; every line plain when absent (the plain figure). */
  classOf?: (id: string) => string;
  /** A location's damage, for its snow and the glow's holes; none on the plain figure. */
  hurtOf?: (id: string) => 'destroyed' | 'crippled' | null;
  /** The frame the plate draws (its layout's), for the looks that fill it. */
  frame: Frame;
}

/**
 * The figure: its defs, its glow, and each location's lines inside the condition layers (the `<u>-figure` group the
 * lens copies - lines only, never the hit areas).
 */
function PersonFigure(props: FigureProps) {
  const { u, art, state, play, guard, drawn, frame, classOf = () => styles.loc, hurtOf = () => null } = props;
  const regions = regionsOf(art);
  const ids = Object.keys(regions.clip);
  const { snowy, holes } = damageMarks(regions, ids, hurtOf);
  const marks = useMemo(() => figureMarks(art), [art]);
  const moving = !useHoldsStill();
  const look = figureLook(u, drawn, play);
  return (
    <>
      <ArtDefs u={u} art={art} glow silhouette={needsSilhouette(drawn)}>
        <LocationClips u={u} regions={regions} />
        <ConditionDefs u={u} s={state} m={marks} />
        {play && <HitDefs u={u} hits={play.burst.hits} />}
        <TraitDefs u={u} d={drawn} />
        {snowy && <SnowDefs u={u} />}
        <GlowHole u={u} regions={holes} />
      </ArtDefs>
      {/* Undying: the last of it will not go out - its glow pulses (P6). A destroyed part keeps no glow. */}
      <g
        className={clsx(look.className, drawn.undying && tx.undyingPulse)}
        mask={holes.length ? `url(#${u}-glowhole)` : undefined}
      >
        <Glow u={u} k={art.glow} />
      </g>
      <TraitUnder u={u} d={drawn} frame={frame} />
      <g id={`${u}-figure`}>
        <g data-idle>
          <TraitBack u={u} d={drawn} play={play} />
          <g filter={look.filter} className={look.className} style={look.style}>
            <g className={look.struck}>
              <g className={look.standing}>
                <FigureMove play={play} art={art}>
                  <ConditionLayers u={u} s={state} overlays={<ConditionOverlays u={u} s={state} m={marks} />}>
                    <FigureLocations {...{ ...props, ids, marks, moving, classOf, hurtOf }} />
                  </ConditionLayers>
                  <TraitOn u={u} d={drawn} />
                  {play && <StrikeMoments u={u} play={play} frame={figureFrame(art)} guard={guard} />}
                  {play && drawn.nightmares && <InkSeals play={play} frame={figureFrame(art)} />}
                </FigureMove>
              </g>
            </g>
          </g>
          <TraitFront u={u} d={drawn} play={play} />
        </g>
      </g>
      <TraitOver u={u} d={drawn} />
      {play && <BurstMarks u={u} play={play} frame={figureFrame(art)} moving={moving} />}
    </>
  );
}

/** What a figure's damage draws: whether any part needs the snow, and the glow's holes for the destroyed ones. */
function damageMarks(regions: Regions, ids: string[], hurtOf: (id: string) => string | null) {
  const snowy = ids.some((id) => hurtOf(id) === 'crippled');
  return {
    snowy,
    holes: holesOf(
      regions,
      ids.filter((id) => hurtOf(id) === 'destroyed'),
    ),
  };
}

type LocationsProps = FigureProps & {
  ids: string[];
  marks: ReturnType<typeof figureMarks>;
  moving: boolean;
  classOf: (id: string) => string;
  hurtOf: (id: string) => 'destroyed' | 'crippled' | null;
};

/** Each location's lines under its condition, struck where a hit lands, and a crippled one's snow. */
function FigureLocations({
  u,
  art,
  state,
  play,
  shownAt,
  guard,
  drawn,
  ids,
  marks,
  moving,
  classOf,
  hurtOf,
}: LocationsProps) {
  return (
    <g data-g="strobe">
      {ids.map((id) => (
        <g
          key={id}
          {...locationCondition(u, state, marks, id)}
          data-limb={id}
          transform={id === 'head' && drawn.blind ? blindTurn(art) : undefined}
        >
          <Struck u={u} id={id} play={play} shownAt={shownAt} moving={moving} soak={guard.soak}>
            {(hit) => <PlateLines u={u} id={id} className={clsx(classOf(id), hit)} />}
          </Struck>
          {hurtOf(id) === 'crippled' && <Snow u={u} clip={id} />}
        </g>
      ))}
    </g>
  );
}

/**
 * The one move an attack's specials make of the whole figure (a shove, a crush, a kick, a tremor), round everything it
 * carries - keyed by the burst only when there is a move, so an ordinary hit never remounts the figure.
 */
function FigureMove({ play, art, children }: { play: HitPlay | null; art: PlateArt; children: ReactNode }) {
  const move = play && moveOf(play, figureFrame(art), [FEET.x, FEET.y]);
  if (!move) return <g>{children}</g>;
  return (
    <g key={play!.id} className={move.className} style={move.style} data-move>
      {children}
    </g>
  );
}

/** Blind: its head turned a little aside, listening (the bench's 3.5 degrees about the neck; plan design-gaps, P6). */
function blindTurn(art: PlateArt): string {
  const [px, py] = wrongPivot('head', figureFrame(art).partBox('head'));
  return `rotate(3.5 ${px} ${py})`;
}

/** Where a figure's hits land: its anchors, each part's box (the Body's own), the whole figure's bounds (per art). */
const figureFrame = (art: PlateArt) => artFrame(art, figureBounds);

interface BodyProps {
  art: PlateArt;
  callouts: PlateCallout[];
  /** Each callout's free spot, the words' scale and the frame (usePlateLayout). */
  layout: CalloutLayout;
  /** The figure's own props, as PersonFigure takes them. */
  figure: Omit<FigureProps, 'art' | 'classOf'>;
  hot: string | null;
  onHot: (id: string | null) => void;
  /** The sights while a called shot is chosen (`sightsFor`), and the place they hold. */
  sights: Targeting | null;
  locked: string | null;
  onLocationContextMenu?: (id: string) => void;
}

/** The figure, its locations, the callouts and the sights - everything that needs the art. */
function PlateBody({ art, callouts, layout, figure, hot, onHot, sights, locked, onLocationContextMenu }: BodyProps) {
  const { state } = figure;
  const regions = regionsOf(art);
  const marksOf = useMemo(() => new Map(callouts.map((c) => [c.id, c.marks])), [callouts]);
  const { partBox } = figureFrame(art);
  return (
    <>
      <PersonFigure
        art={art}
        {...figure}
        classOf={(id) => clsx(marksClass(marksOf.get(id) ?? []), dimmedBy(locked, id), hot === id && styles.locHot)}
        hurtOf={(id) => damageOf(marksOf.get(id) ?? [])}
      />
      {state.helpless && <OpenBrackets locations={callouts} boxOf={partBox} />}
      {sights ? (
        <PlateSights
          locations={hitOrder(callouts, regions)}
          boxOf={partBox}
          hitOf={(id) => regions.hit[id]}
          targeting={sights}
          onHot={onHot}
          onContextMenu={onLocationContextMenu}
          tight={figure.drawn.carapace}
          slips={figure.drawn.strange}
        />
      ) : (
        hitOrder(callouts, regions).map((c) => (
          <PlateHit key={c.id} id={c.id} d={regions.hit[c.id]} onHot={onHot} onContextMenu={onLocationContextMenu} />
        ))
      )}
      {callouts.map((c) => (
        <Callout
          key={c.id}
          c={c}
          at={layout.at[c.id]}
          k={layout.k}
          hot={hot === c.id}
          daemon={figure.drawn.daemon > 0}
        />
      ))}
      {hot && (
        <Readout
          c={callouts.find((c) => c.id === hot)}
          more={lockedLines(hot === locked, figure.drawn.strange)}
          mid={layout.frame.x + layout.frame.w / 2}
        />
      )}
    </>
  );
}

const TAG_CLASS = { destroyed: styles.tagDestroyed, crippled: styles.tagCrippled };

/** A daemon's numbers flicker each on its own beat (seeded by the place), never all at once. */
const flickStep = (id: string): CSSProperties => ({ animationDelay: `${(-3.1 * seeded('flick')(id)).toFixed(2)}s` });

interface CalloutProps {
  c: PlateCallout;
  /** Its column and row (the placement's free spot), and the words' scale about them. */
  at: { x: number; row: number };
  k: number;
  hot: boolean;
  daemon: boolean;
}

/** A callout beside its part: a short leader from the part's dot, its name, the armour (and cover), and its tag. */
function Callout({ c, at: { x, row }, k, hot, daemon }: CalloutProps) {
  const right = c.side === 'R';
  const anchor = right ? 'start' : 'end';
  const tag = damageOf(c.marks);
  return (
    <g className={clsx(styles.callout, hot && styles.hot, c.cover !== null && styles.covered)} data-callout={c.id}>
      <CalloutLeader c={c} x={x} row={row} right={right} nameDy={CALLOUT_DY.name} k={k} />
      <g transform={scaleAbout(x, row, k)}>
        <text
          className={clsx(styles.numeral, daemon && tx.flick)}
          style={daemon ? flickStep(c.id) : undefined}
          x={x}
          y={row + CALLOUT_DY.numeral}
          textAnchor={anchor}
        >
          {c.armour}
          <CoverPlus ap={c.cover} />
        </text>
        {daemon && (
          <text
            className={clsx(styles.numeral, tx.wrongNumber)}
            style={flickStep(c.id)}
            x={x}
            y={row + CALLOUT_DY.numeral}
            textAnchor={anchor}
            aria-hidden
          >
            {wrongNumber(c.id, c.armour)}
          </text>
        )}
        {tag && (
          <text className={clsx(styles.tag, TAG_CLASS[tag])} x={x} y={row + CALLOUT_DY.tag} textAnchor={anchor}>
            {tag.toUpperCase()}
          </text>
        )}
      </g>
    </g>
  );
}

const rangeText = ([lo, hi]: [number, number]) => `${String(lo).padStart(2, '0')}-${hi === 100 ? '00' : hi}`;

/** The location under the pointer, in one line over the figure: name, armour and cover, roll range, tag. */
function Readout({ c, more, mid }: { c: PlateCallout | undefined; more: readonly string[]; mid: number }) {
  if (!c) return null;
  const cover = c.cover === null ? '' : ` +${c.cover} COVER`;
  const tag = damageOf(c.marks)?.toUpperCase();
  return (
    <text className={styles.readout} x={mid} y={-60} textAnchor="middle" data-testid="plate-readout">
      {`${c.name.toUpperCase()} · AP ${c.armour}${cover} · ${rangeText(c.range)}${tag ? ` · ${tag}` : ''}`}
      {more.map((line) => (
        <tspan key={line} x={mid} dy={100}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

/** The lock-on's corner arms (the bench's 90), shared with the live sights. */
const OPEN_ARM = 90;

/** Helpless: every location open to a called shot - its lock-on brackets, fitted to it, shown faintly. */
function OpenBrackets({ locations, boxOf }: { locations: { id: string }[]; boxOf: (id: string) => readonly number[] }) {
  return (
    <g data-testid="plate-open">
      {locations.map((l) => (
        <path key={l.id} className={fxStyles.openBracket} d={boxBracketPath(boxOf(l.id), 0, OPEN_ARM)} />
      ))}
    </g>
  );
}

/** The words under the figure that a count cannot say (statusWords); the wounds are the panel's readout. */
function Status({ state, banished, left }: { state: PlateState; banished: boolean; left: number }) {
  return (
    <StatusLine
      label={statusWords(state, banished)}
      at={[left, 3260]}
      testid="plate-wounds"
      labelClass={styles.woundsLabel}
    />
  );
}

/** The seconds since the last frame, clamped (a hidden tab picks up where it was); none on the first. */
const stepFrom = (last: number, t: number) => (last < 0 ? 0 : Math.min(0.1, Math.max(0, t - last)));

/** The figure's idle as a transform: shifted, turned about its feet, breathing from them. */
function idleTransform(art: PlateArt, T: number, amp: number, thrown: Shift) {
  const m = scaleIdle(idleMotion(art.idle, T, art.id === 'astartes'), amp);
  const turn = `rotate(${m.rot.toFixed(3)} ${FEET.x} ${FEET.y})`;
  const breath = `translate(0 ${FEET.y}) scale(1 ${m.sy.toFixed(4)}) translate(0 ${-FEET.y})`;
  return `translate(${(m.dx + thrown.dx).toFixed(1)} ${(m.dy + thrown.dy).toFixed(1)}) ${turn} ${breath}`;
}

/** A shift of the whole figure, frame units. */
type Shift = { dx: number; dy: number };
const STILL: Shift = { dx: 0, dy: 0 };

/** How fast the idle eases in and out: the share of the way per second (about 0.06 a frame at 60 fps). */
const IDLE_EASE = 3.6;

/** Where the figure sways from: the middle of the frame at its feet. */
const FEET = { x: 1061, y: 2950 };

/**
 * What the plate says in words, as its layout must keep room for it: the last hit (kept after it plays) and its line
 * count, and whether the status line under the figure says anything.
 */
function usePlateWords(impact: Impact | null | undefined, state: PlateState, looks: ReturnType<typeof traitLooks>) {
  const last = useLastImpact(impact);
  const words = last ? lastHitLines(last, looks, String).length : 0;
  return { last, words, status: statusWords(state, looks.daemon > 0) !== '' };
}

/**
 * The figure's idle life on the plate's clock (plan body-map S3): its kind's motion, eased in and out (never a jump),
 * held still under the states whose stillness means something - and once eased out there, the clock may stop. It moves
 * the whole figure - the `data-idle` group, every condition layer inside it, and so the lens's copy too.
 */
function useIdle(art: PlateArt | null, s: PlateState, u: string, paused: boolean) {
  // From rest, always: the first frame eases in from the pose drawn, never a jump.
  const amp = useRef(0);
  const last = useRef(-1);
  // At rest and held there (a still state, eased fully out): the clock may stop.
  const [resting, setResting] = useState(true);
  // The idle group, found once after each render - never searched for per frame.
  const group = useRef<{ setAttribute(name: string, value: string): void } | null>(null);
  useLayoutEffect(() => {
    group.current = null;
  });
  const seed = idleSeed(u);
  const onFrame = (t: number, root: SVGSVGElement, thrown: Shift = STILL) => {
    if (!art) return;
    const dt = stepFrom(last.current, t);
    last.current = t;
    // Idle life gives way to every hit: it eases out while one plays, and back in after.
    const target = Number(!idleStill(s) && !paused);
    amp.current = approach(amp.current, target, dt * IDLE_EASE);
    if (resting !== (amp.current === 0 && target === 0)) setResting(!resting);
    group.current ??= root.querySelector('[data-idle]');
    group.current?.setAttribute('transform', idleTransform(art, t + seed, amp.current, thrown));
  };
  /** The clock stopped (motion off, or nothing left to move): the figure back at the pose drawn. */
  const onStop = (root: SVGSVGElement) => {
    amp.current = 0;
    last.current = -1;
    root.querySelector('[data-idle]')?.removeAttribute('transform');
  };
  // Paused (a hit, or Critical for as long as it lasts) is still once eased out: the clock may stop until it resumes.
  return { moving: (!idleStill(s) && !paused) || !resting, onFrame, onStop };
}

/**
 * One frame of the plate: the hit playing (its filters, a critical's strobe, the throw folded into the idle's
 * transform), then the idle. When the clock stops, the idle's still pose and no strobe.
 */
function useHitFrame(
  hits: ReturnType<typeof usePlateHits>,
  idle: ReturnType<typeof useIdle>,
  traits: TraitFrame | null,
  /** A Sturdy body: the knock barely moves it (the bench's 0.3; plan design-gaps, P5). */
  sturdy: boolean,
) {
  const writerOf = useSceneWriter();
  const onFrame = (t: number, root: SVGSVGElement) => {
    const burst = hits.play?.burst;
    const e = hits.elapsed(t);
    if (burst) writeScene(writerOf(root), hitNodes(burst.hits, e, true));
    if (traits) writeScene(writerOf(root), traits.at(t));
    const by = burst && thrower(burst, sturdy);
    const thrown = by && e >= by.at ? throwAt(e - by.at, by.amp) : STILL;
    idle.onFrame(t, root, thrown);
    traits?.pin(root);
  };
  const onStop = (root: SVGSVGElement) => {
    applyScene(root, [{ key: 'strobe', attrs: { style: '' } }, ...(traits?.still() ?? [])]);
    idle.onStop(root);
    traits?.pin(root, true);
  };
  return { onFrame, onStop };
}

/**
 * An attack's play on the body and its looks as drawn this moment. The play is held until an overloaded field has
 * shattered and every look's own moment on its hits is over; the looks are drawn once per render, for the clock and
 * the drawing alike.
 */
function useLooksPlay(
  impact: Impact | null | undefined,
  target: Target,
  looks: TraitLooks,
  art: PlateArt | null,
  dead: boolean,
) {
  const locations = useMemo(
    () => getBodyConfig(target.bodyType || 'humanoid').locations.map((l) => l.id),
    [target.bodyType],
  );
  const hits = usePlateHits(impact, locations, (burst: Burst) =>
    Math.max(fieldHold(burst.hits), traitHold(looks, burst.hits)),
  );
  const drawn = useMemo(
    () => art && traitDraw(looks, art, guardOf(target), hits.play, dead),
    [looks, art, target, hits.play, dead],
  );
  return { hits, drawn };
}

/** What the plate's root says for a spec and its stylesheet: its arm moment, the places a hit is playing. */
function rootData(moment: 'drop' | 'disarm' | undefined, play: HitPlay | null) {
  return {
    'data-moment': moment,
    'data-hits': play?.burst.hits.map((h) => h.location).join(' ') || undefined,
  };
}

/** The arm moment a condition just gained plays (P6): Disarmed wrenches the weapon away, Drop Weapon lets it fall. */
function useArmMoment(conditions: readonly string[]): 'drop' | 'disarm' | undefined {
  const held = useMemo(() => ({ plate: conditions.map((c) => ({ id: condType(c) })) }), [conditions]);
  const onsets: Record<string, string[]> = useOnsets(held, useHoldsStill(), ARM_MOMENT_MS);
  const gained = onsets.plate ?? [];
  if (gained.includes('Dsm')) return 'disarm';
  return gained.includes('Dr') ? 'drop' : undefined;
}

/** How long an arm moment runs (the bench's drop, the longer of the two). */
const ARM_MOMENT_MS = 1000;
